import { Injectable, inject, signal, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RESPONSIBILITY } from '../models/representative';
import { ActivatedRoute, Router } from '@angular/router';
import { navigateToRelatedClient } from '../../../../core/utils/client-navigation.util';

interface AttornieRow {
  id: number;
  name: string;
  nif: string;
  responsibilityType: string;
  associatedClient?: number | null;
  specialResponsibilitySummary?: string | null;
  startDate?: Date | null;
  endDate?: Date | null;
}

@Injectable()
export class ConstitutionAttorniesStore {
  private readonly clientsService = inject(ClientsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly clientId = signal<number | null>(null);

  setClientId(id: number | null): void {
    this.clientId.set(id);
  }

  readonly attornyOptions = toSignal(
    from(this.clientsService.getClientsNames()),
    { initialValue: [] }
  );

  readonly attornies = signal<AttornieRow[]>([]);
  readonly isModalActive = signal<boolean>(false);
  isEditing = signal<boolean>(false);
  readonly nextId = signal<number>(1);
  editingIndex: number | null = null;

  readonly attorniesForm: FormGroup = this.fb.nonNullable.group({
    id: this.fb.control<number | null>(null),
    client: this.fb.control<number | null>(null),
    associatedClient: this.fb.control<number | null>(null),
    name: this.fb.nonNullable.control<string>({ value: '', disabled: true }),
    nif: this.fb.nonNullable.control<string>({ value: '', disabled: true }),
    responsibilityType: this.fb.nonNullable.control<string>(''),
    specialResponsibilitySummary: this.fb.nonNullable.control<string>(''),
    startDate: this.fb.control<Date | null>(null),
    endDate: this.fb.control<Date | null>(null),
  });

  readonly tableConfig = signal<any | null>(null);

  constructor() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const idParam = params.get('id');
        const id = idParam && idParam !== 'new' ? Number(idParam) : null;
        this.clientId.set(Number.isNaN(id as number) ? null : id);
        if (id) {
          void this.loadAttornies(id);
        }
      });
    this.setupClientChangeListener();
    this.buildTableConfig();
  }

  private async loadAttornies(clientId: number): Promise<void> {
    try {
      const res = await this.clientsService.getClientAttorneys(clientId);
      const list = Array.isArray(res) ? res : (res?.attorneys || res?.attornies || []);
      const mapped = (list || []).map((item: any, index: number) => {
        const associated = item?.associatedClient || item?.client || {};
        const associatedId = item?.associatedClient?.id
          ?? item?.associatedClientId
          ?? item?.associatedClient
          ?? associated?.id
          ?? null;
        const name = item?.name
          || associated?.name
          || [associated?.firstSurname, associated?.secondSurname, associated?.name].filter(Boolean).join(' ');
        const nif = item?.nif || item?.documentNumber || associated?.nif || associated?.documentNumber || '';
        return {
          id: item?.id ?? index + 1,
          name: name || '',
          nif,
          responsibilityType: item?.responsibilityType || item?.responsibility || '',
          associatedClient: associatedId,
          specialResponsibilitySummary: item?.specialResponsibilitySummary ?? null,
          startDate: item?.startDate ? new Date(item.startDate) : null,
          endDate: item?.endDate ? new Date(item.endDate) : null
        } as AttornieRow;
      });
      this.attornies.set(mapped);
      const maxId = mapped.reduce((max, a) => Math.max(max, a.id), 0);
      this.nextId.set(maxId + 1);
      this.buildTableConfig();
    } catch {
      this.attornies.set([]);
      this.buildTableConfig();
    }
  }

  private setupClientChangeListener(): void {
    this.attorniesForm.get('associatedClient')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(id => {
        if (!id) return;
        const selected = this.attornyOptions().find(c => c.id === id);
        if (selected) {
          this.attorniesForm.patchValue({
            name: selected.name,
            nif: selected.nif || selected.documentNumber || ''
          });
        }
      });
  }

  openModal(index?: number): void {
    const currentClientId = this.clientId();
    this.attorniesForm.reset({
      id: null,
      client: currentClientId,
      associatedClient: null,
      name: '',
      nif: '',
      responsibilityType: '',
      specialResponsibilitySummary: '',
      startDate: null,
      endDate: null
    }, { emitEvent: false });

    if (index !== undefined) {
      this.isEditing.set(true);
      this.editingIndex = index; // Set the editing index
      const rep = this.attornies()[index];
      this.attorniesForm.patchValue({
        ...rep,
        associatedClient: rep.associatedClient ?? null,
        client: currentClientId,
        specialResponsibilitySummary: rep.specialResponsibilitySummary ?? '',
        startDate: rep.startDate ?? null,
        endDate: rep.endDate ?? null
      }, { emitEvent: false });
    } else {
      this.isEditing.set(false);
      this.editingIndex = null; // Reset editing index when creating new
    }

    this.isModalActive.set(true);
  }

  closeModal(): void {
    this.isModalActive.set(false);
    this.editingIndex = null;
  }

  saveAttorney(deferPersistence = false): void {
    if (!this.attorniesForm.valid) return;

    const formValue = this.attorniesForm.getRawValue();
    const clientId = this.clientId() ?? formValue.client ?? null;
    const newRep: AttornieRow = {
      id: formValue.id || this.nextId(),
      name: formValue.name,
      nif: formValue.nif,
      responsibilityType: formValue.responsibilityType,
      associatedClient: formValue.associatedClient,
      specialResponsibilitySummary: formValue.specialResponsibilitySummary,
      startDate: formValue.startDate ?? null,
      endDate: formValue.endDate ?? null
    };

    const sharedPayload = {
      associatedClient: formValue.associatedClient!,
      name: formValue.name,
      nif: formValue.nif,
      responsibilityType: formValue.responsibilityType,
      specialResponsibilitySummary: formValue.specialResponsibilitySummary,
      startDate: formValue.startDate ?? null,
      endDate: formValue.endDate ?? null
    };

    if (this.isEditing() && this.editingIndex !== null) {
      const current = this.attornies();
      current[this.editingIndex] = newRep;
      this.attornies.set([...current]);
      if (!deferPersistence && clientId) {
        this.clientsService.updateClientAttorney(newRep.id, {
          id: newRep.id,
          client: clientId,
          ...sharedPayload
        });
      }
    } else {
      this.attornies.set([...this.attornies(), newRep]);
      this.nextId.set(this.nextId() + 1);
      if (!deferPersistence && clientId) {
        this.clientsService.saveClientAttorney({
          client: clientId,
          ...sharedPayload
        });
      }
    }

    this.buildTableConfig();
    this.closeModal();
  }

  deleteAttorney(index: number, deferPersistence = false): void {
    const current = this.attornies();
    const target = current[index];
    this.attornies.set(current.filter((_, i) => i !== index));
    this.buildTableConfig();
    if (deferPersistence || !target?.id) return;
    this.clientsService.deleteClientAttorney(target.id).catch(() => {
      // ignore errors for now
    });
  }

  goToClientDetail(index: number, activeTab: string = 'constitution'): void {
    const row = this.attornies()[index];
    const clientId = row?.associatedClient ?? null;
    if (!clientId) return;
    navigateToRelatedClient(this.router, clientId, activeTab);
  }

  serializeAttorneyForOperation(rep: AttornieRow): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      associatedClient: rep.associatedClient ?? null,
      name: rep.name,
      nif: rep.nif,
      responsibilityType: rep.responsibilityType,
      specialResponsibilitySummary: rep.specialResponsibilitySummary ?? undefined,
      startDate: rep.startDate,
      endDate: rep.endDate,
    };
    if (rep.id > 0) {
      payload['id'] = rep.id;
    }
    return payload;
  }

  serializeAttorneysForOperation(): Record<string, unknown>[] {
    return this.attornies().map(a => this.serializeAttorneyForOperation(a));
  }

  hydrateFromOperation(apoderados: any[] | null | undefined): void {
    if (!apoderados?.length) return;

    const mapped = apoderados.map((item: any, index: number) => ({
      id: item?.id ?? index + 1,
      name: item?.name ?? '',
      nif: item?.nif ?? item?.documentNumber ?? '',
      responsibilityType: item?.responsibilityType || item?.responsibility || '',
      associatedClient: item?.associatedClient ?? null,
      specialResponsibilitySummary: item?.specialResponsibilitySummary ?? null,
      startDate: item?.startDate ? new Date(item.startDate) : null,
      endDate: item?.endDate ? new Date(item.endDate) : null,
    } as AttornieRow));

    this.attornies.set(mapped);
    const maxId = mapped.reduce((max, a) => Math.max(max, a.id), 0);
    this.nextId.set(maxId + 1);
    this.buildTableConfig();
  }

  private buildTableConfig(): void {
    const headers = [
      { name: 'Nombre', key: 'name' },
      { name: 'NIF', key: 'nif' },
      { name: 'Tipo de poder', key: 'responsibilityLabel' },
      { name: 'Fecha de alta', key: 'startDateFormatted' },
      { name: 'Fecha de baja', key: 'endDateFormatted' },
      { name: 'Acciones', key: 'delete' }
    ];

    const data = this.attornies().map(rep => {
      const responsibility = RESPONSIBILITY.find(r => r.value === rep.responsibilityType);
      return {
        ...rep,
        responsibilityLabel: responsibility?.label || rep.responsibilityType,
        startDateFormatted: rep.startDate ? new Date(rep.startDate).toLocaleDateString('es-ES') : '-',
        endDateFormatted: rep.endDate ? new Date(rep.endDate).toLocaleDateString('es-ES') : '-'
      };
    });

    this.tableConfig.set({ headers, data, isPersons: true });
  }
}